import { LegacyCard, DataTable, Layout, Page, LegacyStack, Tag, Link } from '@shopify/polaris';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ChoiceListPickerCustom } from './ChoiceListPickerCustom';
import { DateRangeCustom } from './DateRangeCustom';
import { SkeletonPageCustom } from './SkeletonPageCustom';
import { MoneyFormatCustom } from './MoneyFormatCustom';
import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function Analytics(props) {
    const navigate = useHostNavigate();
    const dateRangeRef = useRef();

    const [loading, setLoading] = useState(true);
    const [model, setModel] = useState({});
    const [reloading, setReloading] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [campaignMethod, setCampaignMethod] = useState('');
    const [campaignStatus, setCampaignStatus] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [sortIndex, setSortIndex] = useState(0);
    const [sortDirection, setSortDirection] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('');
    const [campaignMethods] = useState([
        { label: 'Automatic', value: 'Automatic' },
        { label: 'Code', value: 'Code' },
    ]);
    const [campaignStatuses] = useState([
        { label: 'Active', value: 'Active' },
        { label: 'Scheduled', value: 'Scheduled' },
        { label: 'Expired', value: 'Expired' },
    ]);

    const fetch = FetchCustom();

    const getLocalFiltes = function () {
        var l_startDate = localStorage.getItem(`${props.user.myShopifyDomain}_analytics_startDate`);
        if (!l_startDate) {
            l_startDate = 'allTime';
        }

        var l_endDate = localStorage.getItem(`${props.user.myShopifyDomain}_analytics_endDate`);
        if (!l_endDate) {
            l_endDate = 'allTime';
        }

        var l_campaignMethod = localStorage.getItem(`${props.user.myShopifyDomain}_analytics_campaignMethod`);
        if (!l_campaignMethod) {
            l_campaignMethod = '';
        }

        var l_campaignStatus = localStorage.getItem(`${props.user.myShopifyDomain}_analytics_campaignStatus`);
        if (!l_campaignStatus) {
            l_campaignStatus = '';
        }

        return { startDate: l_startDate, endDate: l_endDate, campaignMethod: l_campaignMethod, campaignStatus: l_campaignStatus };
    }

    const getTableRows = function () {

        var items = model.items;
        var index = sortIndex;
        var direction = sortDirection;
        var moneyFormat = props.user.moneyFormat;

        var columns = ['campaignName', 'campaignStatus', 'impressions', 'applies', 'converted', 'discountAmount'];
        var col = columns[index];

        if (direction) {
            items = items.sort((rowA, rowB) => {
                var itemA = rowA[col];
                var itemB = rowB[col];

                return direction === 'descending' ? itemB - itemA : itemA - itemB;
            });
        }

        var rows = [];

        items.forEach(function (item) {
            rows.push([item.campaignName ? item.campaignName : 'Stack discounts', item.campaignStatusName, item.impressions, item.applies, item.converted, <MoneyFormatCustom price={item.discountAmount} format={moneyFormat} />]);
            //rows.push([<Link removeUnderline url={'/analytics/' + item.campaignId } >{item.campaignName ? item.campaignName : 'Stack discounts'}</Link>, item.campaignStatus, item.impressions, item.applies, item.converted, <MoneyFormatCustom price={item.discountAmount} format={moneyFormat} />]);
        });

        return rows;
    }

    //const sum = function (items, prop) {
    //    return items.reduce(function (a, b) {
    //        return a + b[prop];
    //    }, 0);
    //};

    const getTotals = function () {
        var totals = model.totals;
        var moneyFormat = props.user.moneyFormat;
        return ['', '', totals.impressions, totals.applies, totals.converted, <MoneyFormatCustom price={totals.discountAmount} format={moneyFormat} />];
    }

    const populateData = async () => {
        setReloading(true);

        const data = await fetch(`api/home/analytics?startDate=${startDate}&endDate=${endDate}&campaignMethod=${campaignMethod}&campaignStatus=${campaignStatus}`);

        if (data.reauthorizeUrl) {
            return;
        }

        props.setIsLoading(false);

        if (data.redirectUrl) {
            setRedirectUrl(data.redirectUrl);
        }
        else {
            setModel(data);
            setLoading(false);
            setReloading(false);
        }
    };

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    const handleSort = useCallback(
        (index, direction) => {
            setSortIndex(index);
            setSortDirection(direction);
        },
        [],
    );

    const handleDateRangeTitleChange = useCallback(
        (value) => {
            setDateRange(value === 'All time' ? '' : value);
        },
        [],
    );

    const handleDateRangeChange = useCallback(
        (l_startDate, l_endDate) => {
            setStartDate(l_startDate);
            setEndDate(l_endDate);

            localStorage.setItem(`${props.user.myShopifyDomain}_analytics_startDate`, l_startDate);
            localStorage.setItem(`${props.user.myShopifyDomain}_analytics_endDate`, l_endDate);
        },
        [props.user.myShopifyDomain],
    );

    const handleFilterChange = useCallback(
        (fieldName, value) => {
            if (fieldName === 'dateRange') {
                setDateRange(value);
                if (!value) {
                    dateRangeRef.current.removeSelection();
                }
            }
            else if (fieldName === 'campaignMethod') {
                setCampaignMethod(value);
            }
            else if (fieldName === 'campaignStatus') {
                setCampaignStatus(value);
            }

            localStorage.setItem(`${props.user.myShopifyDomain}_analytics_${fieldName}`, value);
        },
        [props.user.myShopifyDomain],
    );

    const handleFilterClearAll = useCallback(
        () => {
            setDateRange('');
            dateRangeRef.current.removeSelection();
            setCampaignMethod('');
            setCampaignStatus('');

            localStorage.setItem(`${props.user.myShopifyDomain}_analytics_dateRange`, '');
            localStorage.setItem(`${props.user.myShopifyDomain}_analytics_campaignMethod`, '');
            localStorage.setItem(`${props.user.myShopifyDomain}_analytics_campaignStatus`, '');
        },
        [props.user.myShopifyDomain],
    );

    useEffect(() => {
        getUser();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (JSON.stringify(props.user) !== '{}') {
            document.title = "Analytics | Stack Discounts";
            props.setIsLoading(true);

            var lfs = getLocalFiltes();

            setStartDate(lfs.startDate);
            setEndDate(lfs.endDate);
            setCampaignMethod(lfs.campaignMethod);
            setCampaignStatus(lfs.campaignStatus);
        }
        // eslint-disable-next-line
    }, [props.user]);

    useEffect(() => {
        if (startDate !== '' && endDate !== '') {
            populateData();
        }
        // eslint-disable-next-line
    }, [startDate, endDate, campaignMethod, campaignStatus]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    return (
        loading ?
            <SkeletonPageCustom />
            :
            <Page title="Analytics" fullWidth>
                <Layout>
                    <Layout.Section>
                        <LegacyCard>
                            <LegacyCard.Section>
                                <LegacyStack>
                                    <LegacyStack.Item>
                                        <DateRangeCustom
                                            ref={dateRangeRef}
                                            label="Date range"
                                            fieldName="dateRange"
                                            startDate={startDate}
                                            endDate={endDate}
                                            titleInPlace={false}
                                            handleChange={handleDateRangeChange}
                                            handleTitleChange={handleDateRangeTitleChange}
                                        />
                                    </LegacyStack.Item>
                                    <LegacyStack.Item>
                                        <ChoiceListPickerCustom
                                            title={'Campaign method'}
                                            fieldName="campaignMethod"
                                            choices={campaignMethods}
                                            selected={[campaignMethod]}
                                            handleChange={handleFilterChange}
                                        />
                                    </LegacyStack.Item>
                                    <LegacyStack.Item>
                                        <ChoiceListPickerCustom
                                            title={'Campaign status'}
                                            fieldName="campaignStatus"
                                            choices={campaignStatuses}
                                            selected={[campaignStatus]}
                                            handleChange={handleFilterChange}
                                        />
                                    </LegacyStack.Item>
                                </LegacyStack>
                            </LegacyCard.Section>
                            {
                                dateRange || campaignMethod || campaignStatus ?
                                    <LegacyCard.Section subdued>
                                        <LegacyStack>
                                            {
                                                dateRange ?
                                                    <Tag onRemove={() => handleFilterChange('dateRange', '')}>{'Date range is ' + dateRange}</Tag>
                                                    : null
                                            }
                                            {
                                                campaignMethod ?
                                                    <Tag onRemove={() => handleFilterChange('campaignMethod', '')}>{'Campaign method is ' + campaignMethod}</Tag>
                                                    : null
                                            }
                                            {
                                                campaignStatus ?
                                                    <Tag onRemove={() => handleFilterChange('campaignStatus', '')}>{'Campaign status is ' + campaignStatus}</Tag>
                                                    : null
                                            }
                                            <Link monochrome onClick={() => handleFilterClearAll()}>Clear all</Link>
                                        </LegacyStack>
                                    </LegacyCard.Section>
                                    : null
                            }
                            <div className="data-table-custom">
                                {
                                    reloading ?
                                        <div className="data-table-loader"></div>
                                        : null
                                }
                                <DataTable
                                    columnContentTypes={[
                                        'text',
                                        'text',
                                        'numeric',
                                        'numeric',
                                        'numeric',
                                        'numeric',
                                    ]}
                                    headings={[
                                        'Campaign name',
                                        'Campaign status',
                                        'Impressions',
                                        'Applies',
                                        'Converted',
                                        'Discount Amount',
                                    ]}
                                    rows={getTableRows()}
                                    totals={getTotals()}
                                    totalsName={{ plural: 'Summary', singular: 'Summary' }}
                                    sortable={[false, false, true, true, true, true, true, true]}
                                    onSort={(index, direction) => handleSort(index, direction)}
                                    defaultSortDirection="descending"
                                />
                            </div>
                        </LegacyCard>
                    </Layout.Section>
                </Layout>
            </Page >
    );
}